import type { CognitoAuthStorage } from 'amazon-cognito-auth-js';
import { Stage } from 'src/components/common/util/StageHelpers';
import { cognitoTokenStorage } from 'src/components/common/util/inMemoryCognitoStorage';

// Katal deploy-time replacements: the website stack substitutes its own Cognito pool, app client and
// Hosted UI domain prefix into this pre-built bundle when it deploys (see LangHubServiceCDK cloudfront.ts),
// so one artifact authenticates against whichever stage or personal stack it lands on.
export interface DeployTimeCognitoConfig {
  readonly userPoolId: string;
  readonly clientId: string;
  readonly domainPrefix: string;
}

const DEPLOY_TIME_COGNITO: DeployTimeCognitoConfig = {
  userPoolId: '',
  clientId: '',
  domainPrefix: '',
};
const COGNITO_REGION = 'us-west-2';

/** A token the deploy did not replace is still a literal `{{...}}` (or empty). */
const isResolved = (value: string): boolean =>
  value !== '' && !value.startsWith('{{');

export interface AuthConfig {
  AppWebDomain: string;
  ClientId: string;
  RedirectUriSignIn: string;
  RedirectUriSignOut: string;
  TokenScopesArray: Array<string>;
  UserPoolId: string;
  // In-memory storage so the SDK never persists the ID/access/refresh tokens to
  // localStorage. Supplied on every config so both the injected and fallback
  // paths get it. See inMemoryCognitoStorage for the reload/new-tab tradeoff.
  Storage: CognitoAuthStorage;
}

const tokenScopesArray = ['phone', 'email', 'profile', 'openid'];

export function getAuthConfigByStage(
  stage: Stage,
  cognito: DeployTimeCognitoConfig = DEPLOY_TIME_COGNITO,
): AuthConfig {
  const callbackUrl = window.location.origin + '/';

  // The deployed stack's own Cognito config. Redirect URIs come from the live origin (e.g. the
  // *.cloudfront.net domain), which must be registered as an OAuth callback on the app client.
  if (
    isResolved(cognito.userPoolId) &&
    isResolved(cognito.clientId) &&
    isResolved(cognito.domainPrefix)
  ) {
    return {
      AppWebDomain: `${cognito.domainPrefix}.auth.${COGNITO_REGION}.amazoncognito.com`,
      ClientId: cognito.clientId,
      RedirectUriSignIn: callbackUrl,
      RedirectUriSignOut: callbackUrl,
      TokenScopesArray: tokenScopesArray,
      UserPoolId: cognito.userPoolId,
      Storage: cognitoTokenStorage,
    };
  }

  // Unreplaced tokens (a deploy from before the website stack supplied them): keep the per-stage config.
  if (stage === Stage.Test) {
    stage = Stage.Beta;
  }
  const config: AuthConfig = {
    AppWebDomain: 'langhub-' + stage + '.auth.us-west-2.amazoncognito.com',
    RedirectUriSignIn: callbackUrl,
    RedirectUriSignOut: callbackUrl,
    TokenScopesArray: tokenScopesArray,
    UserPoolId: '',
    ClientId: '',
    Storage: cognitoTokenStorage,
  };

  switch (stage) {
    case Stage.Gamma:
      config.UserPoolId = 'us-west-2_yEcuiED3Y';
      config.ClientId = '3ngg1rukohckpbbo3efcs9p27l';
      break;

    case Stage.Prod:
      config.UserPoolId = 'us-west-2_FB8U2uZFS';
      config.ClientId = '3k3jeqtat2do1aopuc3s7bto8n';
      break;

    default:
      config.UserPoolId = 'us-west-2_DZ1RpToC1';
      config.ClientId = '2a6ehulus8m74uacor26mn6k7t';
      break;
  }
  return config;
}
